import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Login } from './components/Login';
import { Register } from './components/Register';
import { PrivateRoute } from './components/PrivateRoute';

// ─────────────────────────────────────────────────────────────────────────────
// CARREGAMENTO POR ROTA
//
// Antes, as 34 páginas eram importadas de uma vez: entrar em qualquer tela baixava
// 2,29 MB de JavaScript — o app inteiro, incluindo Licitações, Restaurante e Mapa —
// antes de desenhar o primeiro pixel. É o que se sentia como "o sistema demora".
//
// Com lazy(), cada rota vira um pedaço próprio, buscado só quando alguém navega para
// ela. A primeira pintura passa a depender do esqueleto mais a tela pedida.
//
// Login, Register e PrivateRoute ficam EAGER de propósito: são o caminho de toda
// entrada no app. Adiar quem sempre carrega não economiza nada e ainda troca um
// atraso por um piscar de tela.
const ModuleHub = lazy(() => import('./components/ModuleHub'));
const ModuleShell = lazy(() => import('./components/ModuleShell'));
const UsersAdmin = lazy(() => import('./components/UsersAdmin'));
const ClientesPage = lazy(() => import('./components/modules/ClientesPage'));
const ProspeccaoPage = lazy(() => import('./components/modules/ProspeccaoPage'));
const CatalogoPage = lazy(() => import('./components/modules/CatalogoPage'));
const FinanceiroPage = lazy(() => import('./components/modules/FinanceiroPage'));
const RestaurantePage = lazy(() => import('./components/modules/RestaurantePage'));
const TarefasPage = lazy(() => import('./components/modules/TarefasPage'));
const FornecedoresPage = lazy(() => import('./components/modules/FornecedoresPage'));
const MetricasPage = lazy(() => import('./components/modules/MetricasPage'));
const OrcamentosPage = lazy(() => import('./components/modules/OrcamentosPage'));
const MapsPage = lazy(() => import('./components/modules/MapsPage'));
const LicitacoesPage = lazy(() => import('./components/modules/LicitacoesPage'));
const AgendaReunioesPage = lazy(() => import('./components/modules/AgendaReunioesPage'));
const LeadsPage = lazy(() => import('./components/modules/LeadsPage'));
const FreelasPage = lazy(() => import('./components/modules/FreelasPage'));
const ExtracaoPage = lazy(() => import('./components/modules/ExtracaoPage'));
const RecursosPage = lazy(() => import('./components/modules/RecursosPage'));
const EmailsPage = lazy(() => import('./components/modules/EmailsPage'));
const ImportarPage = lazy(() => import('./components/modules/ImportarPage'));
const CanaisPage = lazy(() => import('./components/modules/CanaisPage'));
const LinkedInPage = lazy(() => import('./components/modules/LinkedInPage'));
const InstagramPage = lazy(() => import('./components/modules/InstagramPage'));
const BaseWebPage = lazy(() => import('./components/modules/BaseWebPage'));
const MarketplacePage = lazy(() => import('./components/modules/MarketplacePage'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const AIChat = lazy(() => import('./pages/AIChat').then((m) => ({ default: m.AIChat })));
const ConfigPage = lazy(() => import('./pages/ConfigPage'));
const Agents = lazy(() => import('./pages/Agents').then((m) => ({ default: m.Agents })));
const AgentForm = lazy(() => import('./pages/AgentForm').then((m) => ({ default: m.AgentForm })));
const AgentTraining = lazy(() => import('./pages/AgentTraining').then((m) => ({ default: m.AgentTraining })));
const AgentChat = lazy(() => import('./pages/AgentChat'));
const EquipeConsole = lazy(() => import('./pages/EquipeConsole'));
const Postagens = lazy(() => import('./pages/Postagens'));
const Conversations = lazy(() => import('./pages/Conversations/Conversations').then((m) => ({ default: m.Conversations })));
const WhatsAppInbox = lazy(() => import('./components/whatsapp/WhatsAppInbox'));
const Ativar = lazy(() => import('./pages/Ativar'));
const PaginaQr = lazy(() => import('./pages/PaginaQr'));
const QrCodesPage = lazy(() => import('./components/modules/QrCodesPage'));
// FERRAMENTA DE INSPEÇÃO, fora do produto: a matriz de disposições do cartão.
const MatrizCartoes = lazy(() => import('./pages/MatrizCartoes'));
const SugestoesPage = lazy(() => import('./components/modules/SugestoesPage'));
const FontesPublicasPage = lazy(() => import('./components/modules/FontesPublicasPage'));
const NaoFuiEu = lazy(() => import('./pages/NaoFuiEu'));
const WhatsAppHub = lazy(() => import('./components/whatsapp/WhatsAppHub'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Fallback do Suspense. Centralizado e discreto: uma tela cheia de esqueleto para uma
// troca de rota que dura 100 ms chama mais atenção que o próprio atraso.
const TelaCarregando: React.FC = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
                minHeight: '60vh', color: '#94a3b8', fontSize: 14 }}>
    carregando…
  </div>
);

export const AppRoutes: React.FC = () => {
  return (
    <BrowserRouter>
      <Suspense fallback={<TelaCarregando />}>
      <Routes>
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        {/* Entrada por convite. O link do e-mail aponta para cá; a pagina troca o
            token por JWT e entra. Publica de proposito: quem chega por convite
            ainda nao tem sessao. */}
        <Route path="/ativar" element={<Ativar />} />
        {/* Terceira opcao do e-mail de tentativa de acesso. Publica: quem esta
            trancado fora e justamente quem precisa dela. */}
        <Route path="/nao-fui-eu" element={<NaoFuiEu />} />
        {/* Loja/marketplace PÚBLICA (sem login) — checkout demo */}
        <Route path="/loja/:ownerId" element={<MarketplacePage />} />
        {/* A página que o cartão impresso abre. PÚBLICA: quem lê é o cliente do
            balcão, que não tem conta — exigir login mataria o produto. */}
        <Route path="/r/:slug" element={<PaginaQr />} />
        <Route
          path="/app"
          element={
            <PrivateRoute>
              <ModuleHub />
            </PrivateRoute>
          }
        />
        {/* Páginas reais dos módulos (antes do catch-all /app/:key) */}
        {/* ABA NA URL — o padrão do painel desde 22/10/2026. Todo módulo com abas
            tem o par "rota base redireciona para a 1ª aba" + "rota /:aba", e a tela
            resolve o resto com o hook `useAbaNaUrl`. Ver src/hooks/useAbaNaUrl.ts.
            ⚠️ Estas rotas não são enfeite: sem elas, /app/x/<aba> cai no catch-all
            /app/:key, que só casa UM segmento — ou seja, 404 em link compartilhado. */}
        <Route path="/app/dashboard" element={<Navigate to="/app/dashboard/operacao" replace />} />
        <Route path="/app/dashboard/:aba" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
        {/* A aba entra na URL: /app/crm/campanhas abre direto em Campanhas, o link é
            compartilhável e recarregar não perde o lugar. Sem o parâmetro, cai em Fila. */}
        <Route path="/app/crm" element={<Navigate to="/app/crm/fila" replace />} />
        <Route path="/app/crm/:aba" element={<PrivateRoute><ClientesPage /></PrivateRoute>} />
        <Route path="/app/clientes" element={<Navigate to="/app/crm" replace />} />
        {/* PROSPECÇÃO — "quem existe lá fora", separado do CRM em 07/10. Descobrir e
            relacionar eram abas da mesma fileira e são perguntas diferentes. */}
        <Route path="/app/prospeccao" element={<Navigate to="/app/prospeccao/receita" replace />} />
        <Route path="/app/prospeccao/:aba" element={<PrivateRoute><ProspeccaoPage /></PrivateRoute>} />
        <Route path="/app/marketplace" element={<Navigate to="/app/marketplace/produtos" replace />} />
        <Route path="/app/marketplace/:aba" element={<PrivateRoute><CatalogoPage /></PrivateRoute>} />
        <Route path="/app/catalogo" element={<Navigate to="/app/marketplace" replace />} />
        {/* Módulo removido do catálogo em 27/09/2026. A rota vira redirecionamento em
            vez de sumir: link salvo e favorito não devem virar 404 por remoção de menu.
            A API e a tabela (reservas_mod, 0 linhas) continuam de pé — a remoção é de
            navegação, e é reversível. */}
        <Route path="/app/reservas" element={<Navigate to="/app/modulos" replace />} />
        <Route path="/app/financeiro" element={<Navigate to="/app/financeiro/lancamentos" replace />} />
        <Route path="/app/financeiro/:aba" element={<PrivateRoute><FinanceiroPage /></PrivateRoute>} />
        <Route path="/app/restaurante" element={<Navigate to="/app/restaurante/pdv" replace />} />
        <Route path="/app/restaurante/:aba" element={<PrivateRoute><RestaurantePage /></PrivateRoute>} />
        <Route path="/app/qrcodes" element={<PrivateRoute><QrCodesPage /></PrivateRoute>} />
        {/* ⚠️ ATRÁS DO LOGIN. É ferramenta de desenvolvimento, e a partir do momento
            em que vai junto num build publicado, rota pública seria a galeria de artes
            do produto aberta na internet, sem motivo. */}
        <Route path="/matriz-cartoes" element={<PrivateRoute><MatrizCartoes /></PrivateRoute>} />
        <Route path="/app/sugestoes" element={<PrivateRoute><SugestoesPage /></PrivateRoute>} />
        <Route path="/app/fontes" element={<PrivateRoute><FontesPublicasPage /></PrivateRoute>} />
        <Route path="/app/tarefas" element={<PrivateRoute><TarefasPage /></PrivateRoute>} />
        <Route path="/app/fornecedores" element={<Navigate to="/app/fornecedores/fornecedores" replace />} />
        <Route path="/app/fornecedores/:aba" element={<PrivateRoute><FornecedoresPage /></PrivateRoute>} />
        {/* Métricas: Search Console + medidor próprio. Tela única, sem abas na URL. */}
        <Route path="/app/metricas" element={<PrivateRoute><MetricasPage /></PrivateRoute>} />
        <Route path="/app/orcamentos" element={<PrivateRoute><OrcamentosPage /></PrivateRoute>} />
        {/* Estoque deixou de ser módulo: movimento e preço são do Catálogo, e a parte
            de rotas virou o módulo Google Maps. O redirect leva o link antigo ao
            Catálogo, que é onde o estoque passou a morar. */}
        <Route path="/app/estoque" element={<Navigate to="/app/marketplace" replace />} />
        {/* Maps e Terrenos viraram um módulo só, "Mapa": a prospecção acha quem já está
            instalado e as camadas dizem o que o terreno em volta permite — separados,
            ninguém cruzava um com o outro. Os dois caminhos antigos continuam de pé
            porque são link que já circulou, e caem na mesma tela. */}
        <Route path="/app/mapa" element={<PrivateRoute><MapsPage /></PrivateRoute>} />
        <Route path="/app/licitacoes" element={<PrivateRoute><LicitacoesPage /></PrivateRoute>} />
        <Route path="/app/maps" element={<Navigate to="/app/mapa" replace />} />
        <Route path="/app/terrenos" element={<Navigate to="/app/mapa" replace />} />
        {/* Agenda e Reuniões viraram um módulo só, e a aba mora na URL (padrão do
            painel — ver useAbaNaUrl). O módulo ABRE em Reuniões: é onde se transcreve.
            /app/reunioes continua de pé porque é link que já circulou — e agora cai
            direto na aba certa. */}
        <Route path="/app/agenda" element={<Navigate to="/app/agenda/reunioes" replace />} />
        <Route path="/app/agenda/:aba" element={<PrivateRoute><AgendaReunioesPage /></PrivateRoute>} />
        <Route path="/app/reunioes" element={<Navigate to="/app/agenda/reunioes" replace />} />
        {/* Página removida a pedido: as conexões que existem de verdade moram no módulo
            que as usa (Google na Agenda, bancos no Financeiro, Evolution no WhatsApp).
            O redirect fica para não quebrar link salvo. */}
        <Route path="/app/integracoes" element={<Navigate to="/app" replace />} />
        {/* Prospecção virou aba de /app/crm. A rota antiga continua valendo como
            redirecionamento: link salvo e favorito não podem quebrar numa fusão de tela. */}
        <Route path="/app/leads" element={<Navigate to="/app/crm" replace />} />
        <Route path="/app/freelas" element={<PrivateRoute><FreelasPage /></PrivateRoute>} />
        <Route path="/app/extracao" element={<PrivateRoute><ExtracaoPage /></PrivateRoute>} />
        <Route path="/app/recursos" element={<PrivateRoute><RecursosPage /></PrivateRoute>} />
        <Route path="/app/emails" element={<PrivateRoute><EmailsPage /></PrivateRoute>} />
        <Route path="/app/importar" element={<PrivateRoute><ImportarPage /></PrivateRoute>} />
        <Route path="/app/canais" element={<PrivateRoute><CanaisPage /></PrivateRoute>} />
        <Route path="/app/linkedin" element={<Navigate to="/app/linkedin/oportunidades" replace />} />
        <Route path="/app/linkedin/:aba" element={<PrivateRoute><LinkedInPage /></PrivateRoute>} />
        <Route path="/app/instagram" element={<PrivateRoute><InstagramPage /></PrivateRoute>} />
        <Route path="/app/base-web" element={<Navigate to="/app/base-web/buscar" replace />} />
        <Route path="/app/base-web/:aba" element={<PrivateRoute><BaseWebPage /></PrivateRoute>} />
        {/* Atalhos de módulos que reaproveitam páginas existentes */}
        <Route path="/app/automacoes" element={<Navigate to="/agents" replace />} />
        <Route path="/app/atendimento" element={<Navigate to="/whatsapp" replace />} />
        <Route
          path="/app/:key"
          element={
            <PrivateRoute>
              <ModuleShell />
            </PrivateRoute>
          }
        />
        <Route
          path="/usuarios"
          element={
            <PrivateRoute>
              <UsersAdmin />
            </PrivateRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/chat"
          element={
            <PrivateRoute>
              <AIChat />
            </PrivateRoute>
          }
        />
        <Route path="/whatsapp" element={<Navigate to="/whatsapp/conversas" replace />} />
        {/* A aba na URL, igual a /app/crm/:aba. É o que torna possível mandar
            "/whatsapp/conexoes" para alguém ligar um número. */}
        <Route
          path="/whatsapp/:aba"
          element={
            <PrivateRoute>
              <WhatsAppHub />
            </PrivateRoute>
          }
        />
        <Route
          path="/whatsapp/legado"
          element={
            <PrivateRoute>
              <Conversations />
            </PrivateRoute>
          }
        />
        <Route path="/config" element={<Navigate to="/config/conta" replace />} />
        <Route
          path="/config/:aba"
          element={
            <PrivateRoute>
              <ConfigPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/agents"
          element={
            <PrivateRoute>
              <Agents />
            </PrivateRoute>
          }
        />
        <Route
          path="/agents/new"
          element={
            <PrivateRoute>
              <AgentForm />
            </PrivateRoute>
          }
        />
        <Route
          path="/agents/:agentId/edit"
          element={
            <PrivateRoute>
              <AgentForm />
            </PrivateRoute>
          }
        />
        {/* Postagens (fatias 11-14). PAR de rotas: o primeiro redireciona para a 1ª
            aba, o segundo recebe :aba. Sem os dois, useAbaNaUrl não funciona. */}
        <Route path="/app/postagens" element={<Navigate to="/app/postagens/ativos" replace />} />
        <Route
          path="/app/postagens/:aba"
          element={
            <PrivateRoute>
              <Postagens />
            </PrivateRoute>
          }
        />
        {/* Console das equipes de agentes (fatia 4). PAR de rotas: sem o segundo, a
            aba na URL não funciona — ver o padrão de useAbaNaUrl em 12 módulos. */}
        <Route
          path="/app/equipes"
          element={
            <PrivateRoute>
              <EquipeConsole />
            </PrivateRoute>
          }
        />
        <Route
          path="/app/equipes/:equipeId"
          element={
            <PrivateRoute>
              <EquipeConsole />
            </PrivateRoute>
          }
        />
        <Route
          path="/agent-chat/:agentId/:threadId"
          element={
            <PrivateRoute>
              <AgentChat />
            </PrivateRoute>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
};
